import { z } from "zod";
import { Printable } from "./feedback.ts";
import { UserId } from "./user.ts";

/**
 * Группа: именованный список учеников преподавателя.
 *
 * ⚠️ **Рассылка уехала в `./announcements.ts` 29.08.2026** вместе с потолками
 * объявления, ниткой и квитанцией: два предмета в одном файле перестали
 * читаться. Связь односторонняя, `announcements.ts` читает отсюда `GroupId` и
 * `MAILING_LIST_MEMBERS_MAX`, а обратной ссылки нет ни одной.
 *
 * Проект целиком в `docs/group-lessons-design.md`. Решения, от которых зависит
 * форма этих схем:
 *
 * - **группа это именованный список учеников, а не сущность расписания**
 *   (раздел 2). Занятий у неё может не быть вовсе, и рассылочный список это
 *   нормальная группа, а не вырожденная
 * - **`membersVisible` это единственное поле, различающее два вида группы**, и
 *   механика объявления у них одна и та же. Индивидуальная рассылка это N=1 и
 *   отдельной фичей не становится (раздел 8)
 * - **`groupId` у объявления это то, КАК собрали список; истина это список
 *   получателей** (раздел 8). Поэтому объявление без группы законно, а
 *   объявление без получателей нет
 *
 * ⚠️ **Имя внутри группы НЕОБЯЗАТЕЛЬНО, и незаданное разрешается в профильное**
 * (решение владельца 29.08.2026). До него продукт запрещал включить видимость
 * состава, пока преподаватель не задаст имя каждому, и на этом стояло обещание
 * «фамилий в группе нет никогда». Обещание снято вместе с требованием: продукт
 * информирует и не блокирует, то же самое владелец выбрал для согласия
 * (раздел 8 `docs/group-lessons-design.md`).
 *
 * Правило целиком, и оно одно на все поверхности: **заданное имя вытесняет
 * профильное, незаданное показывает профильное.** Считает его `memberNameShown`
 * внизу файла, и она же единственное место, где написано, что пустая строка и
 * пробелы это «не задано».
 *
 * ⚠️ **Цена названа, а не замолчана:** преподаватель, открывший состав, не
 * задав имён, покажет детям полные имена друг друга, вместе с фамилией, если он
 * её записал. Раньше это было невозможно по построению, теперь возможно и
 * объявлено текстом предупреждения (`MEMBERS_VISIBLE_WARNING` в
 * `apps/web/src/features/groups/groups-text.ts`).
 *
 * ⚠️ Что НЕ изменилось: профильное имя по-прежнему не лежит внутри `members`, и
 * дописывать его туда нельзя. Едет оно соседним полем `studentNames`, разбор у
 * `GroupMemberProfile`. Довод стал другим (не «фамилий не бывает», а «экран
 * обязан отличать заданное имя от подставленного»), а форма прежней.
 */

export const GroupId = z.string().uuid();
export type GroupId = z.infer<typeof GroupId>;

/* ──────────────────────────── потолки ──────────────────────────── */

/**
 * Тот же потолок, что у названия урока, заготовки и домашки. Одно число на все
 * названия в продукте: своё здесь не отвечало бы ни на один новый вопрос.
 */
export const GROUP_TITLE_MAX = 200;

/**
 * Потолок размера УЧЕБНОЙ группы, то есть только той, у которой
 * `membersVisible = true` (раздел 2 проекта). Ограничение двустороннее, и оба
 * его довода про занятие. Панельная причина: агрегат ответов с пятнадцатью
 * именами под каждым вариантом перестаёт быть панелью и становится списком, то
 * есть теряет то единственное, ради чего делается. Ёмкостная причина: комната
 * на двенадцать с включёнными камерами съедает медиасервер целиком (раздел 7
 * проекта).
 *
 * ⚠️ **На рассылочный список это число не распространяется**, и распространять
 * его туда значит применить довод к сущности, у которой нет ни занятия, ни
 * комнаты, ни панели: канонический рассылочный список это «все мои
 * девятиклассники», и двенадцатью он не ограничен ничем. Своё число у него
 * следующей константой.
 *
 * ⚠️ Отсюда следствие, которое легко потерять: **включение `membersVisible` у
 * списка длиннее двенадцати это отказ `group_too_big_to_reveal`**, а не тихое
 * превращение в учебную группу на тридцать человек. Проверяется на переключении
 * флага, а не только на добавлении участника, и код у него СВОЙ, а не общий с
 * добавлением: «некуда добавлять» и «слишком много людей, чтобы показывать
 * имена» лечатся разным (разбор у обоих кодов в `./errors.ts`).
 *
 * Отказ, а не усечение: молча не добавленный ученик это молчаливая деградация,
 * которую запрещает 5.5 CLAUDE.md.
 */
export const GROUP_MEMBERS_MAX = 12;

/**
 * Потолок РАССЫЛОЧНОГО списка (`membersVisible = false`).
 *
 * Своё число, потому что довод у него свой и он единственный: сколько людей
 * адресуется одним объявлением. Ни занятия, ни комнаты, ни камер, ни панели
 * ответов у такой группы нет вовсе, поэтому ни панельный, ни ёмкостный довод
 * учебной группы сюда не переносится.
 *
 * Шестьдесят это записная книжка преподавателя с запасом, а не оценка группы:
 * при полной занятости учеников у него десятки (год истории у преподавателя с
 * двенадцатью учениками это около семисот работ, `HOMEWORK_PAGE_LIMIT_MAX`), а
 * рассылочный список это её подмножество. Потолок здесь стоит затем, чтобы одна
 * рассылка оставалась одной ограниченной вставкой и одним обозримым списком
 * квитанций, а не затем, чтобы кого-то сдержать.
 *
 * Тем же числом ограничена поимённая адресация одного объявления
 * (`ANNOUNCEMENT_RECIPIENTS_MAX` в `./announcements.ts`), и это намеренно:
 * группа и поимённый список кладут одни и те же строки получателей.
 */
export const MAILING_LIST_MEMBERS_MAX = 60;

/** Столько же, сколько у `DisplayName`: это имя человека, просто другое. */
export const GROUP_MEMBER_NAME_MAX = 80;

/**
 * Редакция текста предупреждения, которое видит преподаватель перед включением
 * `membersVisible`. Записывается строкой журнала `student_group_warning` и
 * заодно парой `student_group.warned_at` и `warned_text_rev`.
 *
 * ⚠️ **Это метка ИНФОРМИРОВАНИЯ, а не согласия**, и называть её согласием
 * нельзя (решение владельца 29.08.2026). Правка текста предупреждения обязана
 * двигать это значение тем же коммитом: иначе запись перестаёт означать, что
 * именно человек прочитал.
 *
 * ⚠️ **Каждая редакция это своя строка журнала, и прошлые не переписываются**
 * (02.09.2026). Пара полей на строке группы отвечает на вопрос «под какой
 * формулировкой раскрыт состав СЕЙЧАС» и переписывается на каждом раскрытии
 * СОСТАВА; журнал отвечает на вопрос «какие тексты этому человеку показывали»,
 * и ответ на него обязан быть полным. Отсюда и `warnedCurrentRev` у карточки:
 * он считается по журналу, а не сравнением с колонкой.
 *
 * ⚠️ **Открытие ОТВЕТОВ ни того, ни другого не пишет** (правка по итогам ревью
 * 02.09.2026). Свежую редакцию оно требует, потому что перед ним стоит свой
 * текст той же редакции, но след информирования у обеих записей один и тот же:
 * «под какими словами открыт состав». Пока предикат был общий, открытие ответов
 * у давно видимой группы переписывало пару полей сегодняшним числом и гасило
 * `MEMBERS_VISIBLE_AGAIN_WARNING` навсегда, так и не показав его.
 *
 * ⚠️ **Сам текст живёт в `apps/web/src/features/groups/groups-text.ts`,
 * значением рядом с этой редакцией, а не разметкой в компоненте** (правка по
 * итогам ревью 29.08.2026). Пока он стоял JSX-абзацами, связь с редакцией
 * держалась комментарием, то есть правка слова проходила зелёный `pnpm check` и
 * колонка `warned_text_rev` начинала врать. Теперь редакцию и текст сшивает
 * тест `groups-text.test.ts`: он считает хеш текста и сверяет его с редакцией,
 * поэтому правка слова роняет прогон, пока эта строка не сдвинута.
 *
 * `2026-08-29`: текст переписан под достроенную ученическую сторону. Прежняя
 * редакция обещала раскрытие имён, которого продукт не делал: ученик не видел
 * ни состава, ни имён. Теперь состав приезжает ему списком получателей в
 * карточке сообщения, а при открытых ответах он видит и ответы остальных, и
 * предупреждение говорит именно это.
 *
 * `2026-08-29b`: имя внутри группы перестало быть обязательным (решение
 * владельца того же дня), и текст больше не обещает «фамилий мы не
 * показываем»: незаданное имя означает, что дети увидят друг друга так, как
 * они записаны в списке учеников.
 *
 * ⚠️ **Метка того же дня, а не следующего, и это не лень.** Редакция
 * `2026-08-29` уже уехала на стенд, значит подменить её хеш нельзя, нужна
 * НОВАЯ строка. Но и поставить `2026-08-30` нельзя: сегодня 29.08.2026, а дата
 * в будущем это ровно та описка, которую проект стережёт тестом в «Что нового».
 * Отсюда буква: она различает две редакции одного дня и ни о чём не врёт.
 *
 * `2026-08-30`: состав переехал из карточки сообщения в шапку окна переписки, и
 * вместе с ним переехал вопрос, на который он отвечает. Было «ученик увидит под
 * сообщением список тех, кому оно пришло», стало «ученик увидит, кто ещё в этой
 * группе»: список собирается по составу группы СЕЙЧАС, а не по получателям
 * прошлой рассылки. Второй текст поправлен тем же словом («каждый видит, кто ещё
 * в неё входит»), потому что редакция у них одна.
 *
 * `2026-09-02`: у занятий группы появилась общая комната, а войти в неё можно
 * только с открытыми именами. Прежние редакции обещали раскрытие имён и
 * переписки, то есть буквы на экране; теперь дети на занятии видят и слышат
 * друг друга, и молчать об этом текст не может. Отсюда же третий текст,
 * `MEMBERS_VISIBLE_AGAIN_WARNING`: он адресован тем, кто открыл состав под
 * старой редакцией, у кого комната поэтому уже есть и кому её никто не
 * обещал.
 *
 * `2026-09-10`: у каждого занятия появился свой чат, и дети пишут в него друг
 * другу. Прежние редакции обещали, что переписки между учениками у нас нет
 * вовсе, и текст про ответы говорил это прямым текстом. Отсюда же перемена в
 * карточке переинформирования: она перестала требовать включённой комнаты,
 * потому что чат есть у всех, а абзац про комнату внутри неё стал условным.
 */
export const MEMBERS_VISIBLE_WARNING_REV = "2026-09-10";

/* ───────────────────────────── группа ──────────────────────────── */

/**
 * ⚠️ **`Printable` здесь обязателен, а не аккуратен**, и довод тот же, что у
 * тела объявления (`./announcements.ts`): это свободный текст от человека, а
 * PostgreSQL не хранит нулевой байт ни в `text`, ни в `jsonb`, то есть вставка
 * падает пятисоткой (разбор у самого `Printable` в `./feedback.ts`). Название
 * группы и имя участника набирают руками и вставляют из чужих документов ровно
 * так же, как заметку.
 */
export const GroupTitle = Printable.trim().min(1).max(GROUP_TITLE_MAX);
export type GroupTitle = z.infer<typeof GroupTitle>;

/**
 * Имя участника внутри группы.
 *
 * ⚠️ `min(1)` здесь стоять НЕ может, и это то же правило, что у `BoardName`:
 * пустая строка законна и означает «имя ещё не задано». Это состояние, в
 * котором участник заводится, и с 29.08.2026 оно же законное состояние
 * участника ВИДИМОЙ группы: незаданное имя разрешается в профильное
 * (`memberNameShown`), а не запрещает раскрытие.
 */
export const GroupMemberName = Printable.trim().max(GROUP_MEMBER_NAME_MAX);
export type GroupMemberName = z.infer<typeof GroupMemberName>;

/**
 * Участник группы, то есть человек, который в ней СЕЙЧАС.
 *
 * ⚠️ **Ключ строки это `studentUserId`, и ничего кроме** (решение владельца
 * 29.08.2026). Раньше строка была ЗАХОДОМ, ключом была пара
 * `(studentUserId, joinedAt)`, и после «ушёл и вернулся» в составе лежали ДВЕ
 * строки одного человека; здесь стояло предупреждение, что `key={studentUserId}`
 * молча потеряет одну из них. Предупреждения больше нет, потому что нет и
 * случая: вышедший из состава исчезает, вернувшийся приходит одной строкой, и
 * `key={studentUserId}` теперь правильный способ рисовать этот список.
 *
 * Когда человека включали и исключали, лежит в журнале
 * (`student_group_member_event`), у которого своей ручки пока нет: состав и
 * история это разные вопросы, и смешивать их в одном массиве значило требовать
 * от каждого читателя помнить про предикат активности.
 */
export const GroupMember = z.object({
  studentUserId: UserId,
  /**
   * Имя, под которым участника видят остальные. Пустая строка означает «не
   * задано»: `null` здесь был бы вторым способом сказать то же самое.
   *
   * ⚠️ «Не задано» это НЕ «остальные не увидят ничего»: с 29.08.2026 такого
   * участника показывают профильным именем (`memberNameShown`). Поэтому поле
   * отвечает на вопрос «что задал преподаватель», а не «что увидит ребёнок», и
   * подставлять сюда профильное на сервере нельзя: экран перестанет отличать
   * заданное имя от подставленного.
   */
  displayName: z.string(),
});
export type GroupMember = z.infer<typeof GroupMember>;

/**
 * Как ученика зовут в списке ПРЕПОДАВАТЕЛЯ (`app_user.display_name`).
 *
 * ⚠️ **Отдельная форма, а не поле внутри `GroupMember`, и это единственная
 * причина, по которой она существует** (правка по итогам ревью 29.08.2026,
 * довод переписан 29.08.2026 вместе со снятием требования задавать имя).
 * Дописать профильное имя в состав было бы на строку короче, и оба довода
 * против этого в силе.
 *
 * Первый: `GroupMember.displayName` отвечает на вопрос «что задал
 * преподаватель». Слив два имени в одно поле, экран перестаёт отличать
 * заданное имя от подставленного, то есть перестаёт показывать преподавателю,
 * что именно увидят дети и что он может это изменить.
 *
 * Второй: при `membersVisible = true` состав однажды покажут и ученику
 * (врезка у `StudentGroupSummary`), и ученическая форма обязана собираться из
 * `members`. Показать там профильное имя теперь законно, но решает это ОДНО
 * место сборки (`memberNameShown` плюс сборщик ответа), а не то, что нужное
 * поле случайно оказалось под рукой.
 *
 * Зачем вообще. В составе группы лежит имя, которое задал преподаватель для
 * ОСТАЛЬНЫХ УЧЕНИКОВ («Ваня»), а на своём экране он ищет человека глазами по
 * тому имени, под которым завёл его сам («Иван Петров»). До этой формы экран
 * сшивал одно с другим вторым запросом в список учеников, и не доехавший
 * список превращал состав в строчки «ученик не из вашего списка».
 */
export const GroupMemberProfile = z.object({
  studentUserId: UserId,
  displayName: z.string(),
});
export type GroupMemberProfile = z.infer<typeof GroupMemberProfile>;

/**
 * Строка списка групп. Состава здесь нет: список читается целиком, а состав
 * нужен одному открытому экрану.
 *
 * ⚠️ **Обе формы группы, `StudentGroupSummary` и `StudentGroupCard`, целиком
 * ПРЕПОДАВАТЕЛЬСКИЕ**, и это не наблюдение о сегодняшних ручках, а требование к
 * завтрашним. Ни `repliesVisible`, ни `warnedAt`, ни `warnedTextRev`, ни
 * `warnedCurrentRev`, ни `roomEnabled` ученику не адресованы: первое это
 * настройка, следующие три это наш след информирования ПРЕПОДАВАТЕЛЯ, а
 * последнее это его собственная колонка включения. Всё это сведения о нём, а не
 * о группе. Сегодня обещание держится тем, что у `repo/groups.ts` единственный
 * предикат это `teacher_user_id = :я`.
 *
 * Ловушка ровно одна и она наступит: при `membersVisible = true` состав
 * показывается и ученику тоже. **Показывать его надо ОТДЕЛЬНОЙ формой** (имена
 * участников и ничего больше), а не этой: `StudentGroupCard.members` лежит
 * рядом с полями выше, и «отдам карточку, лишнее клиент не покажет» это ровно
 * тот способ, которым правило видимости однажды применяют не то.
 *
 * ⚠️ С 29.08.2026 на карточке лежат и имена из списка учеников преподавателя
 * (`studentNames`: имя на паре, а без него профильное, 10.1
 * docs/account-adopt-design.md), а рядом лежит след информирования
 * преподавателя. Собирать ученическую форму
 * надо ИЗ `members`: не потому, что профильное имя ученику показывать нельзя
 * (с того же дня можно, если преподаватель не задал своего), а потому, что
 * решать это обязано одно место сборки, и оно не здесь.
 */
export const StudentGroupSummary = z.object({
  id: GroupId,
  title: z.string(),
  membersVisible: z.boolean(),
  repliesVisible: z.boolean(),
  createdAt: z.number().int(),
  archivedAt: z.number().int().nullable(),
  /** Столько людей в группе сейчас. */
  memberCount: z.number().int().nonnegative(),
  /**
   * Столько из них может заниматься: пара с преподавателем не отвязана.
   *
   * ⚠️ **Определение живой пары здесь не своё, а серверное.** Живым участника
   * считает `bunchCandidates` (`apps/api/src/repo/schedule-groups.ts`): у него
   * пуст `teacher_student.unlinked_at`. Ровно этих участников берёт в серии
   * заведение расписания, а на пустом наборе отвечает `group_schedule_empty`
   * (`apps/api/src/routes/schedule/group-series.ts`). Заведись у числа своё
   * определение, экран сказал бы «расписание завести можно» там, где кнопка
   * отказывает.
   *
   * ⚠️ **Обещание заперто тестом, а не только этой врезкой** (правка по итогам
   * ревью 30.08.2026): `groups.dbtest.ts` сверяет число заведённых серий с этим
   * полем и проверяет отказ на нуле. До того связь держали три ссылки в
   * комментариях, то есть правка отбора участников оставила бы набор зелёным.
   *
   * Зачем поле вообще. Отвязка из группы НЕ выкидывает (раздел 3
   * `docs/group-lessons-design.md`), поэтому `memberCount` может состоять из
   * одних отвязанных, и цену такой группы преподаватель узнавал бы отказом на
   * кнопке. Ученику это поле не адресовано, как и вся форма (врезка выше).
   */
  activeMemberCount: z.number().int().nonnegative(),
});
export type StudentGroupSummary = z.infer<typeof StudentGroupSummary>;

/**
 * Открытая группа: то же самое плюс состав и след информирования.
 *
 * ⚠️ Форма преподавательская целиком, см. врезку у `StudentGroupSummary`.
 */
export const StudentGroupCard = StudentGroupSummary.extend({
  /**
   * ⚠️ **Только преподавателю.** Когда ЕМУ показали предупреждение о раскрытии
   * имён и какой редакции был текст. Не согласие. Экрану это нужно затем, чтобы
   * не показывать предупреждение второй раз при уже показанной текущей
   * редакции; ученику это сведения о преподавателе, а не о его группе.
   */
  warnedAt: z.number().int().nullable(),
  warnedTextRev: z.string().nullable(),
  /**
   * ⚠️ **Показывали ли этому преподавателю ТЕКУЩУЮ редакцию текста по ЭТОЙ
   * группе.** Считается по журналу `student_group_warning`, а не сравнением
   * `warnedTextRev` с `MEMBERS_VISIBLE_WARNING_REV`, и разница между этими
   * двумя ответами и есть смысл поля.
   *
   * Пара выше переписывается на каждом раскрытии состава, поэтому у группы,
   * состав которой открыт давно, она навсегда остаётся редакцией того дня. Ни
   * переименование, ни открытие ответов её не двигают. Сравнение с
   * ней сказало бы «человек не читал», и это правда, но починить её нечем:
   * раскрытие уже случилось, второй раз его не сделать. Журнал же принимает
   * новую строку без всякого раскрытия, поэтому вопрос «показать ли текст
   * заново» задаётся ему.
   *
   * `false` у скрытой группы означает то же, что и всегда: текст покажут перед
   * включением. `false` у ВИДИМОЙ означает, что человека информировали другими
   * словами, и экран показывает ему, что изменилось
   * (`MEMBERS_VISIBLE_AGAIN_WARNING`).
   */
  warnedCurrentRev: z.boolean(),
  /**
   * ⚠️ **Включена ли у ЭТОГО ПРЕПОДАВАТЕЛЯ общая комната занятий группы
   * вообще** (`app_user.group_room_since` непуст). Не «есть ли комната у
   * занятий этой группы»: у конкретного занятия она открывается ещё и датой
   * (`groupRoomOpen`), и такого ответа карточка группы дать не может, потому что
   * занятий не знает.
   *
   * Зачем поле. Владелец может погасить комнату конкретному человеку, это
   * документированный выключатель (шапка миграции `1789430400000_group_room_default`).
   * У такого преподавателя кнопки входа нет ни на одном занятии, и рассказывать
   * ему про общую комнату нельзя ни строкой: `MEMBERS_VISIBLE_AGAIN_WARNING`
   * говорил бы про перемену, которой у него не случилось, а его дверь («не нужна
   * комната, скройте имена») предлагала бы погасить то, чего нет.
   *
   * ⚠️ Поле ПРЕПОДАВАТЕЛЬСКОЕ, как и весь этот файл: ученику оно не адресовано,
   * это сведения о его преподавателе.
   */
  roomEnabled: z.boolean(),
  /** Только те, кто в группе сейчас: вышедшего в составе больше нет. */
  members: z.array(GroupMember),
  /**
   * ⚠️ **Только преподавателю: те же люди его собственными именами.** Строки
   * ровно на тех же участников, что в `members`, и в том же порядке.
   *
   * Почему СБОКУ, а не полем внутри `GroupMember`: врезка у
   * `GroupMemberProfile`. Коротко: `members` отвечает на вопрос «что задал
   * преподаватель», и слив два имени в одно поле, экран перестаёт отличать
   * заданное имя от подставленного.
   *
   * ⚠️ **Необязательное, и это не про старый бандл** (правка по итогам ревью
   * 29.08.2026). Односторонние поля этого файла и соседнего объявлены
   * необязательными все до одного (`groupTitle`, `senderName`, `recipients`), а
   * это одно стояло обязательным, хотя начинка у него самая дорогая: полные
   * имена детей вместе со следом информирования преподавателя рядом.
   * Обязательность означала, что ученическую форму карточки можно собрать,
   * отдав `StudentGroupCard` целиком и понадеявшись, что клиент лишнее не
   * покажет.
   *
   * ⚠️ **Необязательность эту ветку НЕ запрещает, и обещать обратное нельзя.**
   * Здесь стояло «теперь такая ветка не компилируется», и это была неправда:
   * `.optional()` разрешает поле не класть и ничего не говорит про положить, а
   * единственный сегодняшний сборщик (`cardBody`) кладёт его всегда. Ученическая
   * ручка, написанная как `return cardBody(view)`, соберётся и отдаст и
   * профильные имена, и след информирования преподавателя. Обещанный
   * компилятором отказ хуже молчания: следующий поверит, что его подстрахуют
   * (раздел 5.3 CLAUDE.md ровно про это).
   *
   * Что защищает на самом деле: одно место сборки, предикат
   * `teacher_user_id = :я` во всех пяти ручках и замок в `access.dbtest.ts`, где
   * участник группы стоит среди посторонних. Настоящей границей была бы отдельная
   * ученическая схема без этого поля, и завести её надо тем же коммитом, что
   * ученический экран состава.
   */
  studentNames: z.array(GroupMemberProfile).optional(),
});
export type StudentGroupCard = z.infer<typeof StudentGroupCard>;

/**
 * Список групп целиком, без курсора: групп у преподавателя десятки, а
 * страничный список пришлось бы листать ради того, чтобы найти в нём одну.
 */
export const StudentGroupListResponse = z.object({
  groups: z.array(StudentGroupSummary),
  /**
   * Осталось ли за страницей хоть одна группа.
   *
   * ⚠️ **Признак, а не молчание** (правка по итогам ревью 29.08.2026, тот же
   * приём, что у `ConversationListResponse`). Потолок выдачи это 150 строк
   * (`GROUP_LIST_LIMIT` в `apps/api/src/repo/group-list.ts`, посчитан в байтах), и
   * до этого поля сто пятьдесят первая группа была не просто недостижима: об
   * этом не говорилось ни слова, потому что сказать было нечего. «Десятки групп
   * у преподавателя» это не оценка сверху, а оценка учебных групп: рассылочных
   * списков заводят много.
   *
   * Курсор остаётся долгом. Признак его не заменяет, он лишь превращает
   * молчаливое усечение в названное (5.5 CLAUDE.md).
   */
  truncated: z.boolean(),
});
export type StudentGroupListResponse = z.infer<typeof StudentGroupListResponse>;

export const CreateGroupRequest = z.object({
  title: GroupTitle,
});
export type CreateGroupRequest = z.infer<typeof CreateGroupRequest>;

/**
 * Правка группы. Отсутствие поля означает «не трогать».
 *
 * ⚠️ Включение `membersVisible` это МОМЕНТ РАСКРЫТИЯ данных одного ребёнка
 * другому: до него состав никому, кроме преподавателя, не виден. Поэтому у
 * этого поля есть условие, которого нет у остальных: предупреждение показано, и
 * его редакция приезжает в `warningRev`.
 *
 * ⚠️ **Условия «у всех задано имя» больше нет** (решение владельца 29.08.2026),
 * и код отказа `group_member_name_required` снят из словаря вместе с ним.
 * Незаданное имя раскрытию не мешает, оно меняет то, ЧТО увидят дети, и об этом
 * говорит предупреждение. Второе условие, размер группы, осталось.
 */
export const UpdateGroupRequest = z.object({
  title: GroupTitle.optional(),
  membersVisible: z.boolean().optional(),
  /**
   * Включается только вместе с `membersVisible`: видеть ответы группы, не видя
   * её состава, бессмысленно. Держится ещё и `CHECK` в схеме.
   */
  repliesVisible: z.boolean().optional(),
  /**
   * ⚠️ **Убирание в архив ГАСИТ раскрытие состава** (решение владельца
   * 29.08.2026): вместе с `archived: true` сервер выключает и `membersVisible`,
   * и `repliesVisible`, а возврат из архива их не воскрешает. Прислать
   * `archived: true` вместе с `membersVisible: true` это отказ `group_archived`,
   * а не выбор одного из двух: у убранной группы состав раскрытым не бывает.
   */
  archived: z.boolean().optional(),
  /**
   * Редакция показанного предупреждения, обязательна при включении
   * `membersVisible`. Значение сверяется с `MEMBERS_VISIBLE_WARNING_REV`:
   * старая вкладка со старым текстом не должна оставлять запись о том, что
   * человек прочитал текущую редакцию.
   */
  warningRev: z.string().max(64).optional(),
});
export type UpdateGroupRequest = z.infer<typeof UpdateGroupRequest>;

/**
 * Человеку показали предупреждение заново, и он его закрыл.
 *
 * ⚠️ **Отдельная ручка, а не поле в `UpdateGroupRequest`, и это не вкусовщина.**
 * У этого запроса нет ни одного поля группы: он ничего в ней не меняет, он
 * дописывает строку в журнал информирования. Приехав полем PATCH, он попал бы в
 * `nextGroupState`, то есть в функцию, которая считает СЛЕДУЮЩЕЕ СОСТОЯНИЕ
 * СТРОКИ группы, и ей пришлось бы вернуть «состояние прежнее, но запись
 * сделать».
 *
 * Зачем вообще. Раскрытие состава у давно открытой группы уже случилось, второй
 * раз его не сделать, а текст предупреждения с тех пор стал говорить про
 * общую комнату. Показать новый текст можно, записать факт показа некуда:
 * `warned_at` и `warned_text_rev` отвечают на вопрос «под какими словами
 * открыт состав», и переписать их значило бы соврать в единственном поле,
 * заведённом ради правды.
 */
export const RecordGroupWarningRequest = z.object({
  /**
   * Редакция того текста, который человек прочитал. Сверяется с
   * `MEMBERS_VISIBLE_WARNING_REV`, как и у PATCH: вкладка, открытая до правки
   * текста, показала ДРУГИЕ слова, и запись о прочтении текущих была бы
   * неправдой.
   */
  warningRev: z.string().max(64),
});
export type RecordGroupWarningRequest = z.infer<typeof RecordGroupWarningRequest>;

export const AddGroupMemberRequest = z.object({
  studentUserId: UserId,
  /** Отсутствие означает «имя задам потом»: участник заводится безымянным. */
  displayName: GroupMemberName.optional(),
});
export type AddGroupMemberRequest = z.infer<typeof AddGroupMemberRequest>;

export const UpdateGroupMemberRequest = z.object({
  displayName: GroupMemberName,
});
export type UpdateGroupMemberRequest = z.infer<typeof UpdateGroupMemberRequest>;

/**
 * Каким именем участника видят ОСТАЛЬНЫЕ ученики.
 *
 * ⚠️ **Единственное место, где записано правило «заданное имя вытесняет
 * профильное, незаданное показывает профильное»** (решение владельца
 * 29.08.2026). Читателей у правила двое, и оба зовут эту функцию: на сервере её
 * зовёт `authorNameFor`, который подписывает и автора сообщения, и каждую
 * строку состава группы в шапке окна переписки; на клиенте её зовёт панель
 * состава на экране преподавателя.
 *
 * ⚠️ **Зеркала на SQL, которое приходилось сверять, больше нет** (правка
 * 30.08.2026). Третьим читателем был список получателей у ученика (`rosterOf` в
 * снятой карточке объявления), выраженный `case` прямо в запросе, и совпадение
 * двух написаний стерёг `announcements.dbtest.ts`. Карточка снята вместе с
 * лентой, состав переехал в шапку окна и собирается тем же `authorNameFor`
 * (`membersOf` в `apps/api/src/repo/conversation-messages.ts`). То же выражение
 * осталось в SQL ровно в одном месте, `listMembers` в
 * `apps/api/src/repo/groups.ts`, и только в `ORDER BY`: там оно решает порядок
 * строк, а не то, что человек прочитает.
 *
 * ⚠️ Заводя новое зеркало, вернёшь и обязанность его сверять, причём не с этой
 * функцией, а с обёрткой: `authorNameFor` добавляет две ветки, которых тут нет
 * (вышедший из группы и исключение «своё имя себе»), и обе достижимы в составе
 * группы.
 *
 * ⚠️ **Состояний у человека ЧЕТЫРЕ, а параметра здесь два, и четвёртое сюда не
 * входит намеренно** (уточнение по итогам ревью 29.08.2026). Имя задано, имя не
 * задано, нет и профильного, и вышел из группы; последнее это не имя, а
 * отсутствие строки участия, и выражается оно этажом выше, `undefined` в
 * `groupName` у `authorNameFor`. Обязательность обоих параметров здесь и есть
 * тот забор: у вышедшего подпись это `ANNOUNCEMENT_UNKNOWN_AUTHOR`, а не
 * профильное имя (преподаватель уже не может задать ему имя в группе), и
 * решается это до вызова. Дописав сюда третий параметр вида «а в группе ли он»,
 * это различие переносят внутрь и теряют.
 *
 * ⚠️ **Пробелы это не имя, и обе стороны считают так одинаково.** В базе то же
 * самое держит `CHECK (display_name = btrim(display_name))`, а без совпадения
 * один пробел в поле показал бы остальным пустое место вместо подписи.
 *
 * Пустая строка на выходе означает «назвать нечем»: не задано ни имя в группе,
 * ни профильное. Что подставить вместо неё, решает вызывающий, потому что
 * заглушка у переписки своя (`ANNOUNCEMENT_UNKNOWN_AUTHOR`), а у экрана состава
 * своя.
 */
export function memberNameShown(groupName: string, profileName: string): string {
  const own = groupName.trim();
  return own === "" ? profileName.trim() : own;
}
